import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../utils/firebase';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  jobFunction: string;
  role?: string;
  photoURL?: string;
  phoneNumber?: string;
}

interface AuthContextType {
  currentUser: User | null;
  userRole: string | null;
  userProfile: UserProfile | null;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userRole: null,
  userProfile: null,
  logout: async () => {},
  refreshUserProfile: async () => {},
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<AuthContextType['userProfile']>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (user: User) => {
    try {
      // Fetch user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      console.log('User doc exists:', userDoc.exists());

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User role:', userData.role);
        setUserRole(userData.role);
        setUserProfile({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email,
          jobFunction: userData.jobFunction || '',
          photoURL: userData.photoURL || '',
          phoneNumber: userData.phoneNumber || ''
        });
      } else {
        console.log('No user document found');
        setUserRole(null);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserRole(null);
      setUserProfile(null);
    }
  };

  const refreshUserProfile = async () => {
    if (currentUser) {
      await fetchUserData(currentUser);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user?.email);

      setCurrentUser(user);

      if (user) {
        await fetchUserData(user);
      } else {
        console.log('No user logged in');
        setUserRole(null);
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      setUserRole(null);
      setUserProfile(null);
      await signOut(auth);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const value = {
    currentUser,
    userRole,
    userProfile,
    logout,
    refreshUserProfile,
    loading
  };

  console.log('Auth context state:', {
    hasUser: !!currentUser,
    userRole,
    loading
  });

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

